import React from 'react';
import DataGrid, { Column, FilterRow, Paging, Scrolling, Sorting } from 'devextreme-react/data-grid';
import { format, parseISO } from 'date-fns';
import { GrantActivityItemDto } from '../../../../dtos/GrantActivityItemDto';
import GrantDetailCommentTooltip from '../../GrantDetailCommentTooltip';

interface OverviewViewProps {
    items: GrantActivityItemDto[];
    loading: boolean;
    onFileClick: (fileId: string, fileName: string) => Promise<void>;
}

function OverviewView({ items, loading, onFileClick }: OverviewViewProps) {
    const renderType = (cellData: any) => {
        const item: GrantActivityItemDto = cellData.data;
        const icon = item.type === 'File' ? 'dx-icon-file' : 'dx-icon-comment';
        return <i className={icon} title={item.type} />;
    };

    const renderName = (cellData: any) => {
        const item: GrantActivityItemDto = cellData.data;
        if (item.type === 'File' && item.fileId && item.fileName) {
            return (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onFileClick(item.fileId!, item.fileName!);
                    }}
                    title={item.fileName}
                >
                    {item.fileName}
                </a>
            );
        }
        const html = item.commentText ?? '';
        const plain = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        const preview = plain.length > 120 ? plain.substring(0, 120) + '...' : plain;
        const targetId = `OverviewComment${item.id.replace(/-/g, '')}`;
        return (
            <div>
                <div id={targetId}>
                    <i className="dx-icon-comment"></i> {preview}
                </div>
                <GrantDetailCommentTooltip
                    commentText={html}
                    commentUser={item.author}
                    commentDate={toDate(item.date) ? format(toDate(item.date)!, 'MM/dd/yyyy h:mm a') : null}
                    target={targetId}
                />
            </div>
        );
    };

    const toDate = (value: any): Date | null => {
        if (!value) return null;
        if (value instanceof Date) return value;
        const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const renderDate = (cellData: any) => {
        const date = toDate(cellData.value);
        if (!date) return null;
        return <span>{format(date, 'MM/dd/yyyy h:mm a')}</span>;
    };

    return (
        <div style={{ padding: '10px' }}>
            <DataGrid
                dataSource={items}
                height={window.innerHeight - 200}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                columnAutoWidth={false}
                wordWrapEnabled={false}
                noDataText={loading ? 'Loading...' : 'No files or comments found.'}
            >
                <FilterRow visible={true} applyFilter="auto" />
                <Sorting mode="multiple" />
                <Paging defaultPageSize={50} />
                <Scrolling mode="virtual" />

                <Column
                    dataField="type"
                    caption=""
                    width={40}
                    cellRender={renderType}
                    allowFiltering={true}
                    allowSorting={false}
                />
                <Column
                    dataField="date"
                    caption="Date"
                    width={160}
                    dataType="date"
                    sortOrder="desc"
                    sortIndex={0}
                    cellRender={renderDate}
                />
                <Column
                    dataField="module"
                    caption="Module"
                    width={150}
                />
                <Column
                    dataField="phase"
                    caption="Phase"
                    width={160}
                />
                <Column
                    dataField="step"
                    caption="Step"
                    width={160}
                />
                <Column
                    dataField="fileName"
                    caption="Name / Content"
                    cellRender={renderName}
                    calculateFilterExpression={(filterValue: string, selectedFilterOperation: string) => {
                        return [
                            ["fileName", selectedFilterOperation || "contains", filterValue],
                            "or",
                            ["commentText", selectedFilterOperation || "contains", filterValue]
                        ];
                    }}
                />
                <Column
                    dataField="author"
                    caption="Author"
                    width={150}
                />
            </DataGrid>
        </div>
    );
}

export default OverviewView;
